/**
 * Tutoriel d'accueil, guidé par la mascotte (demande de Benoit, 18 août 2026).
 *
 * ══ Non bloquant, par principe ═══════════════════════════════════════════════
 *
 * E§2 interdit la modale de tutoriel — et Benoit veut un guide pas à pas. Les deux
 * se concilient : une BULLE en coin d'écran, le jeu entièrement jouable derrière,
 * chaque étape pointant le vrai menu (l'entrée du dock pulse doucement). Les étapes
 * qui correspondent à une action mesurable se valident TOUTES SEULES quand l'action
 * se produit — le « Suivant » n'existe que pour celles qu'on ne peut pas mesurer.
 * « Passer » est toujours là : un guide qu'on ne peut pas congédier est un mur.
 *
 * ══ La mascotte ══════════════════════════════════════════════════════════════
 *
 * Le pack POLYGON MINI City ne contient AUCUN personnage (vérifié : pas un seul
 * `SM_Chr_*`). La mascotte est donc dessinée pour le jeu — un petit contremaître
 * rond au casque de chantier, dans le registre du jeu d'icônes maison. Le jour où
 * le pack « POLYGON Mini Characters » entre au dépôt, elle se remplace par un rendu
 * 3D sans toucher à la mécanique.
 */

import { t } from '@/i18n';
import type { CitySnapshot } from '@/core/gameState';
import { dayNumber } from '@/core/quests';
import type { ShellSection } from './shell';
import { createButton } from './components';

const STORAGE_KEY = 'villopia:tutorial:v1';

interface TutorialStep {
  readonly id: string;
  readonly textKey: string;
  /** Entrée du dock à faire pulser — le doigt de la mascotte. */
  readonly section?: ShellSection;
  /** L'étape se valide seule quand ce prédicat devient vrai. Sinon, bouton Suivant. */
  readonly doneWhen?: (snapshot: CitySnapshot) => boolean;
}

/**
 * Le parcours voulu par Benoit : construire, agrandir, s'inscrire, regarder une pub.
 * L'accueil ouvre, la conclusion referme — six moments, jamais plus de deux phrases.
 */
const STEPS: readonly TutorialStep[] = [
  { id: 'welcome', textKey: 'tutorial.welcome' },
  {
    id: 'build',
    textKey: 'tutorial.build',
    section: 'build',
    doneWhen: (snapshot) => snapshot.buildingsPlaced >= 1,
  },
  {
    id: 'land',
    textKey: 'tutorial.land',
    section: 'city',
    doneWhen: (snapshot) => snapshot.landPurchasesTotal >= 1,
  },
  { id: 'account', textKey: 'tutorial.account', section: 'social' },
  {
    id: 'ad',
    textKey: 'tutorial.ad',
    doneWhen: (snapshot) => snapshot.adsDay === dayNumber(Date.now()) && snapshot.adsWatched >= 1,
  },
  { id: 'done', textKey: 'tutorial.done' },
];

/** La mascotte : un contremaître rond, casque de chantier, sourire. SVG maison. */
const MASCOT_SVG = `
<svg viewBox="0 0 64 64" role="img" aria-hidden="true">
  <circle cx="32" cy="36" r="22" fill="#ffd9a8"/>
  <path d="M10 32a22 14 0 0 1 44 0l-2 3H12z" fill="#f7b23b"/>
  <rect x="26" y="10" width="12" height="8" rx="3" fill="#f7b23b"/>
  <circle cx="24" cy="38" r="3.2" fill="#233"/>
  <circle cx="40" cy="38" r="3.2" fill="#233"/>
  <path d="M25 47q7 6 14 0" stroke="#233" stroke-width="3" fill="none" stroke-linecap="round"/>
  <circle cx="20" cy="44" r="2.6" fill="#ff9d7a" opacity=".55"/>
  <circle cx="44" cy="44" r="2.6" fill="#ff9d7a" opacity=".55"/>
</svg>`;

export interface TutorialHandle {
  readonly el: HTMLElement;
  /** Fait avancer les étapes auto-validées. Appelé sur chaque nouveau snapshot. */
  update(snapshot: CitySnapshot): void;
  destroy(): void;
}

export interface TutorialOptions {
  /** Fait pulser (ou non) une entrée du dock. */
  readonly highlight: (section: ShellSection | null) => void;
  /**
   * Le guide a été mené jusqu'au bout — pas abandonné, pas déjà fini (G§7.5).
   *
   * Trois façons d'arriver à `finish()` et une seule mérite un cadeau : passer la
   * dernière étape. « Passer le guide » et « le guide était déjà terminé au chargement »
   * n'ont rien accompli, et récompenser le second offrirait un cadeau à chaque
   * rechargement de page — le contraire d'une première fois.
   */
  readonly onCompleted?: () => void;
}

function readProgress(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const value = raw === null ? 0 : Number.parseInt(raw, 10);
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

function writeProgress(step: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(step));
  } catch {
    // Sans stockage, le guide reviendra — mieux que de ne jamais venir.
  }
}

export function createTutorial(options: TutorialOptions): TutorialHandle {
  const el = document.createElement('aside');
  el.className = 'tutorial';
  // Un guide, pas une alerte : il n'interrompt pas les lecteurs d'écran.
  el.setAttribute('role', 'complementary');

  const mascot = document.createElement('div');
  mascot.className = 'tutorial__mascot';
  mascot.innerHTML = MASCOT_SVG;

  const text = document.createElement('p');
  text.className = 'tutorial__text';

  const next = createButton({
    label: t('tutorial.next'),
    variant: 'primary',
    onClick: () => {
      advance();
    },
  });
  next.el.classList.add('tutorial__next');

  const skip = createButton({
    label: t('tutorial.skip'),
    variant: 'ghost',
    onClick: () => {
      finish();
    },
  });
  skip.el.classList.add('tutorial__skip');

  const actions = document.createElement('div');
  actions.className = 'tutorial__actions';
  actions.append(skip.el, next.el);

  const body = document.createElement('div');
  body.className = 'tutorial__body';
  body.append(text, actions);

  el.append(mascot, body);

  let step = readProgress();

  function finish(): void {
    step = STEPS.length;
    writeProgress(step);
    options.highlight(null);
    el.hidden = true;
  }

  function advance(): void {
    // La dernière étape est franchie AVANT que `step` ne sorte du tableau : c'est le seul
    // instant où l'on sait que le guide a été terminé, et non quitté.
    const completing = step === STEPS.length - 1;
    step += 1;
    writeProgress(step);
    if (step >= STEPS.length) {
      finish();
      if (completing) options.onCompleted?.();
      return;
    }
    render();
  }

  function render(): void {
    const current = STEPS[step];
    if (current === undefined) {
      finish();
      return;
    }
    el.hidden = false;
    text.textContent = t(current.textKey as Parameters<typeof t>[0]);
    options.highlight(current.section ?? null);
    // Les étapes auto-validées attendent l'ACTION : le bouton dit « je le ferai
    // plus tard » plutôt que « suivant » — on peut avancer, jamais être coincé.
    next.setLabel(current.doneWhen === undefined ? t('tutorial.next') : t('tutorial.later'));
    // La dernière étape se referme : son bouton le dit.
    if (current.id === 'done') next.setLabel(t('tutorial.finish'));
  }

  if (step >= STEPS.length) {
    el.hidden = true;
    options.highlight(null);
  } else {
    render();
  }

  return {
    el,
    update(snapshot) {
      const current = STEPS[step];
      if (current?.doneWhen === undefined) return;
      if (current.doneWhen(snapshot)) advance();
    },
    destroy() {
      options.highlight(null);
      el.remove();
    },
  };
}
